import {
  Box,
  Card,
  Divider,
  styled,
  Typography as TypographyBase,
  useTheme,
} from '@mui/material';
import { CurrencyAmount } from '@cowbell/ui/core';
import { Flexbox } from '../../../../../../components/layout/Flexbox';
import { formatPolicySummaryData } from '../policy-details.utils';
import { getDeductibleLanguage } from '../../../../../../i18n/utils';
import { ProductTypes, type ProductType } from '../../../../../../types';
import { P100_RETRO_MAPPING } from '../../../../../workflows/quotes/prime-100/utils/index';

interface PolicySummaryPolicy {
  readonly limit?: number | null;
  readonly deductible?: number | null;
  readonly totalPremium?: number | null;
  /** ISO 4217 code supplied by the API alongside the policy. */
  readonly currency: string;
  readonly effectiveDate?: string | null;
  readonly expiresOn?: string | null;
  readonly issuedDate?: string | null;
  readonly uiTerm?: string | number | null;
  readonly waitingPeriod?: number | null;
  readonly retroactivePeriod?: string | number | null;
}

interface PolicySummaryProps {
  readonly policy: PolicySummaryPolicy;
  readonly product?: ProductType | null;
}

export const PolicySummary = ({
  policy,
  product = null,
}: PolicySummaryProps) => {
  const { palette } = useTheme();

  const {
    limit,
    deductible,
    totalPremium,
    currency,
    effectiveDate,
    expiresOn,
    issuedDate,
    uiTerm,
    waitingPeriod,
    retroactivePeriod,
  } = policy;

  const isP100 = product === ProductTypes.p100;

  const uiWaitingPeriod =
    waitingPeriod == null ? '-' : `${waitingPeriod} Hours`;

  const uiRetroactivePeriod =
    retroactivePeriod == null
      ? '-'
      : P100_RETRO_MAPPING[
          retroactivePeriod as keyof typeof P100_RETRO_MAPPING
        ] ?? '-';

  const { uiEffectiveDate, uiExpiresOn, uiIssuedDate } =
    formatPolicySummaryData({
      effectiveDate,
      expiresOn,
      issuedDate,
    });

  return (
    <PolicySummaryCard square>
      <Box>
        <Typography variant="subtitle2" color="primary">
          POLICY SUMMARY
        </Typography>
      </Box>
      <Divider style={{ width: '100%', margin: '0.5rem 0 1rem 0' }} />
      <Box>
        <Flexbox>
          <Box width="50%" marginRight={1}>
            <Flexbox>
              <Typography variant="caption">Aggregate Limit</Typography>
              <Typography>
                <CurrencyAmount
                  amount={limit}
                  currency={currency}
                  minimumFractionDigits={0}
                />
              </Typography>
            </Flexbox>
            <Flexbox>
              <Typography>{getDeductibleLanguage(product)}</Typography>
              <Typography>
                <CurrencyAmount
                  amount={deductible}
                  currency={currency}
                  minimumFractionDigits={0}
                />
              </Typography>
            </Flexbox>
            <Flexbox>
              <Typography style={{ color: palette.text.text12 }}>
                Total Premium
              </Typography>
              <Typography>
                <CurrencyAmount
                  amount={totalPremium}
                  currency={currency}
                  currencySign="accounting"
                />
              </Typography>
            </Flexbox>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box width="50%" marginLeft={1}>
            <Flexbox>
              <Typography>Policy Issued</Typography>
              <Typography>{uiIssuedDate}</Typography>
            </Flexbox>
            <Flexbox>
              <Typography>Policy Effective</Typography>
              <Typography>{uiEffectiveDate}</Typography>
            </Flexbox>
            <Flexbox>
              <Typography>Policy Expiration</Typography>
              <Typography>{uiExpiresOn}</Typography>
            </Flexbox>
            <Flexbox>
              <Typography>Policy Term</Typography>
              <Typography>{uiTerm} days</Typography>
            </Flexbox>
            {isP100 && (
              <>
                <Flexbox>
                  <Typography>Waiting Period</Typography>
                  <Typography>{uiWaitingPeriod}</Typography>
                </Flexbox>
                <Flexbox>
                  <Typography>Retroactive Period</Typography>
                  <Typography>{uiRetroactivePeriod}</Typography>
                </Flexbox>
              </>
            )}
          </Box>
        </Flexbox>
      </Box>
    </PolicySummaryCard>
  );
};

const Typography = ({ children, ...props }: any) => (
  <TypographyBase variant="caption" {...props}>
    {children}
  </TypographyBase>
);

const PolicySummaryCard = styled(Card)(({ theme: { palette } }) => ({
  padding: '1rem',
  backgroundColor: palette.background.default,
}));
